import { useBreakpoint } from "@povio/ui";
import type { Key } from "react-aria-components";

import type { TextInputVariantProps } from "@/components/ui/inputs/TextInput/TextInputWrapper";

import { DropdownDesktop } from "./DropdownDesktop";
import { MobileDropdown } from "./DropdownMobile";

export type DropdownItem<TKey extends Key = Key> = {
  id: TKey;
  label: string;
};

export type SingleDropdownProps<TKey extends Key = Key> = {
  value: TKey | undefined;
  onChange: (value: TKey) => void;
  selectionMode?: "single";
  showSelectionBar?: never;
  showAllOption?: never;
};

export type MultiDropdownProps<TKey extends Key = Key> = {
  value: TKey[];
  onChange: (value: TKey[]) => void;
  selectionMode: "multiple";
  showSelectionBar?: boolean;
  showAllOption?: boolean;
};

type GroupedDropdownProps<TKey extends Key = Key> = MultiDropdownProps<TKey> | SingleDropdownProps<TKey>;

export type DropdownProps<TKey extends Key = Key> = GroupedDropdownProps<TKey> &
  TextInputVariantProps & {
    label: string;
    placeholder: string;
    items: DropdownItem<TKey>[];
    showSelectionBar?: boolean;
    showAllOption?: boolean;
    customTrigger?: React.ReactElement;
    customTriggerWidth?: number;
    className?: string;
    containerClassName?: string;
    width?: "full" | "fit";
    "data-testid"?: string;
    hideLabels?: boolean;
    required?: boolean;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    useMobileDrawer?: boolean;
  };

export const Dropdown = <TKey extends Key = Key>({ selectionMode = "single", ...dProps }: DropdownProps<TKey>) => {
  const props = {
    selectionMode,
    ...dProps,
  } as DropdownProps<TKey>;

  const isDesktop = useBreakpoint("md");

  if (isDesktop) {
    return <DropdownDesktop {...props} />;
  }

  return <MobileDropdown {...props} />;
};
